import React, { FC, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { getHeldSlugs } from 'utils/entitlements/buildStoreEntitlementRows';
import { plural } from 'utils/plural';
import {
  ENTITLEMENT_ACTIONS,
  ENTITLEMENT_LABELS,
  ENTITLEMENT_PACKAGE_KEYS,
  ENTITLEMENT_REASON_MAX_LENGTH,
  ENTITLEMENT_SOURCES,
} from 'constants/entitlements/entitlements';
import {
  EntitlementAction,
  EntitlementChange,
  EntitlementPackageKey,
  EntitlementSlug,
  StoreEntitlementRow,
} from 'types/entitlements/entitlements';

interface EntitlementChangeDialogProps {
  open: boolean;
  action: EntitlementAction;
  stores: StoreEntitlementRow[];
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (change: EntitlementChange) => void;
}

const PACKAGE_KEYS: EntitlementPackageKey[] = Object.values(ENTITLEMENT_PACKAGE_KEYS);

/**
 * Confirmation step for a grant or revocation (COS-4844). Every change needs a reason: it is what
 * lets the audit trail answer "why", and CentsBE refuses the request without one.
 */
export const EntitlementChangeDialog: FC<EntitlementChangeDialogProps> = ({
  open,
  action,
  stores,
  isSubmitting,
  onCancel,
  onConfirm,
}) => {
  const isGrant = action === ENTITLEMENT_ACTIONS.GRANT;
  const [packageKey, setPackageKey] = useState<EntitlementPackageKey | null>(null);
  const [slugs, setSlugs] = useState<EntitlementSlug[]>([]);
  const [reason, setReason] = useState('');

  const revocableSlugs = getHeldSlugs(stores);
  // Provisioning narrows what a store sees: a fallback store shows everything until its first
  // grant, and a store revoked down to no slugs shows none of the gated features.
  const fallbackStoreCount = stores.filter(
    ({ source }) => source === ENTITLEMENT_SOURCES.FALLBACK,
  ).length;
  const emptiedStoreCount = stores.filter(
    (store) => store.slugs.length > 0 && store.slugs.every((slug) => slugs.includes(slug)),
  ).length;
  const trimmedReason = reason.trim();
  const hasSelection = isGrant ? !!packageKey : slugs.length > 0;
  const canConfirm = hasSelection && !!trimmedReason && !isSubmitting;

  const toggleSlug = (slug: EntitlementSlug): void => {
    setSlugs((current) =>
      current.includes(slug) ? current.filter((item) => item !== slug) : [...current, slug],
    );
  };

  const handleConfirm = (): void => {
    if (isGrant && packageKey) {
      onConfirm({ packageKey, reason: trimmedReason });
    } else if (!isGrant) {
      onConfirm({ slugs, reason: trimmedReason });
    }
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isGrant ? 'Grant' : 'Revoke'} entitlements for {stores.length} store(s)?
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {stores.map(({ name }) => name).join(', ')}
        </Typography>

        {isGrant ? (
          <>
            <FormLabel id="entitlement-package-label">Package</FormLabel>
            <RadioGroup
              aria-labelledby="entitlement-package-label"
              value={packageKey ?? ''}
              onChange={(event) => setPackageKey(event.target.value as EntitlementPackageKey)}
            >
              {PACKAGE_KEYS.map((key) => (
                <FormControlLabel
                  key={key}
                  value={key}
                  control={<Radio />}
                  label={ENTITLEMENT_LABELS[key]}
                />
              ))}
            </RadioGroup>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
              LaundroWorks does not include Connect. Grant only the package the store purchased.
            </Typography>
            {fallbackStoreCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {plural(
                  fallbackStoreCount,
                  '1 store is on fallback and sees',
                  `${fallbackStoreCount} stores are on fallback and see`,
                )}{' '}
                every feature today. After this grant they see only the gated features{' '}
                {packageKey ? ENTITLEMENT_LABELS[packageKey] : 'the granted package'} unlocks.
              </Alert>
            )}
          </>
        ) : (
          <>
            <FormLabel component="legend">Entitlements to revoke</FormLabel>
            <FormGroup sx={{ mb: 1 }}>
              {revocableSlugs.map((slug) => (
                <FormControlLabel
                  key={slug}
                  control={
                    <Checkbox checked={slugs.includes(slug)} onChange={() => toggleSlug(slug)} />
                  }
                  label={ENTITLEMENT_LABELS[slug]}
                />
              ))}
            </FormGroup>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
              Stores that do not hold a selected entitlement are left unchanged.
            </Typography>
            {emptiedStoreCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {plural(emptiedStoreCount, '1 store', `${emptiedStoreCount} stores`)} will hold no
                entitlements and see none of the gated features. They do not return to fallback.
              </Alert>
            )}
          </>
        )}

        <TextField
          label="Reason"
          required
          fullWidth
          multiline
          minRows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          inputProps={{ maxLength: ENTITLEMENT_REASON_MAX_LENGTH }}
          helperText={`${reason.length}/${ENTITLEMENT_REASON_MAX_LENGTH}`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isGrant ? 'primary' : 'error'}
          onClick={handleConfirm}
          disabled={!canConfirm}
        >
          {isGrant ? 'Confirm grant' : 'Confirm revoke'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
